import { z } from "zod";

const heroStatTileSchema = z.object({ value: z.string(), label: z.string() });

export const eventSettingsSchema = z.object({
  title: z.string(),
  // Optional, because every other layer already says so: isValidEvent wraps it
  // in isOptionalBoundedString, CREATE_EVENT omits it when the operator leaves
  // the field blank, and both event pages render it conditionally. Requiring it
  // here made a blank Location fail the parse, and this parse is what gates the
  // WHOLE admin page — including the Location field that would have fixed it.
  location: z.string().optional(),
  description: z.string().optional(),
  slug: z.string().optional(),
  startDate: z.string(),
  endDate: z.string(),
  timezone: z.string().optional(),
  image: z.string().optional(),
  imageThumb: z.string().optional(),
  imageMedium: z.string().optional(),
  imageFocalX: z.number().optional(),
  imageFocalY: z.number().optional(),
  configAgenda: z.boolean().optional(),
  configSpeakers: z.boolean().optional(),
  configAttendees: z.boolean().optional(),
  configPartners: z.boolean().optional(),
  configVenues: z.boolean().optional(),
  configResources: z.boolean().optional(),
  configInformation: z.boolean().optional(),
  configNow: z.boolean().optional(),
  // TNG-96: the "After the event" section. Absent means off.
  configAfterEvent: z.boolean().optional(),
  // The command that booked or sent its "summary is ready" notification.
  afterEventNotificationCommandId: z.string().optional(),
  configConnections: z.boolean().optional(),
  registrationSecretEnabled: z.boolean().optional(),
  // TNG-92: table switch requests. Absent means off.
  trackSwitchEnabled: z.boolean().optional(),
  registrationSecret: z.string().optional(),
  published: z.boolean().optional(),
  // Written only by the archive command. Absent means false.
  archived: z.boolean().optional(),
  // The bold landing page (option D). All optional: an event without them
  // shows the plain hero. Written straight to the event document, bounded by
  // firestore.rules. A malformed stored value reads as absent rather than
  // failing the parse, because this parse gates the WHOLE admin page,
  // including the controls that would repair it.
  heroKicker: z.string().optional().catch(undefined),
  heroHeadline: z.string().optional().catch(undefined),
  heroSubline: z.string().optional().catch(undefined),
  heroStats: z
    .object({
      a: heroStatTileSchema.optional(),
      b: heroStatTileSchema.optional(),
      c: heroStatTileSchema.optional(),
    })
    .optional()
    .catch(undefined),
  heroVideo: z.string().optional().catch(undefined),
  dayTitles: z.record(z.string(), z.string()).optional().catch(undefined),
  attendeeCount: z.number().optional(),
  confirmedCount: z.number().optional(),
  createdAt: z.any().optional(),
});
export type EventSettings = z.infer<typeof eventSettingsSchema>;

export const adminAttendeeSchema = z.object({
  id: z.string(),
  name: z.string(),
  role: z.string().optional(),
  company: z.string().optional(),
  avatar: z.string().optional(),
  bio: z.string().optional(),
  linkedinUrl: z.string().optional(),
  trackId: z.string().optional(),
});
export type AdminAttendee = z.infer<typeof adminAttendeeSchema>;

export const adminCommandSchema = z.object({
  type: z.string(),
  requestedBy: z.string(),
  requestedAt: z.any(),
  eventId: z.string(),
  status: z.enum(["REQUESTED", "IN_PROGRESS", "SUCCEEDED", "FAILED"]),
  error: z.string().optional(),
  payload: z.record(z.string(), z.unknown()).optional(),
  // D21 MINT_INVITE settles with the token it chose and its invite id, so the
  // issuing UI can both show the share text and address a later revoke. Only
  // that command writes them; everything else leaves the keys out.
  token: z.string().optional(),
  inviteId: z.string().optional(),
  // DELETE_EVENT_ANNOUNCEMENT settles with this when an earlier delete
  // command for the same announcement is still running.
  deferredTo: z.string().optional(),
  // SUGGEST_SEATING settles with how many seats it left as they were,
  // because somebody changed them while it ran.
  keptSeats: z.number().optional().catch(undefined),
  expiresAt: z.any().optional(),
});
export type AdminCommand = z.infer<typeof adminCommandSchema>;
